import { useState, useRef, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Clock, Eye, Trash2, Pencil, Users } from 'lucide-react';
import { useServiceViewModel } from '../../view-model/professional/company/service-view-model';
import { Button } from '../ui/button';
import { useTranslation } from 'react-i18next';
import Switch from '../ui/switch';
import { useOnboardingViewModel } from '../../view-model/onboarding/onboarding-view-model';
import { SECTION_SERVICES } from '../../constants/onboarding-sections';
import { getFilteredServices } from '../../components/onboarding/mock/services-mock';

const ServiceGrid = observer(() => {
  const viewModel = useServiceViewModel();
  const { t } = useTranslation();
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [showCollaboratorsPopover, setShowCollaboratorsPopover] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Récupérer l'état d'onboarding en utilisant l'OnboardingViewModel
  const onboardingViewModel = useOnboardingViewModel();

  const shouldShowMockData = !onboardingViewModel.getSectionOnboardingStatus(SECTION_SERVICES)
  && onboardingViewModel.getCurrentOnboardingStep(SECTION_SERVICES) === 2;

  const services = shouldShowMockData
    ? getFilteredServices(viewModel.searchTerm, viewModel.statusFilter)
    : viewModel.filteredServices;

  // Fermer le popover quand on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setShowCollaboratorsPopover(null);
      }
    };

    if (showCollaboratorsPopover) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCollaboratorsPopover]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 services-view">
      {services.map((service) => (
        <div 
          key={service.id} 
          className={`rounded-lg shadow-md overflow-hidden border ${service.isActive 
            ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700' 
            : 'bg-gray-100 dark:bg-gray-900 border-gray-300 dark:border-gray-600 opacity-75'}`}
          onClick={() => setConfirmDelete(null)}
        >
          <div className={`relative h-48 ${service.isActive ? 'bg-gray-100 dark:bg-gray-700' : 'bg-gray-200 dark:bg-gray-800'}`}>
            {service.image ? (
              <img 
                src={service.image} 
                alt={service.name} 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <span className="text-gray-400 dark:text-gray-500">{t('services.noImage')}</span>
              </div>
            )}
            {!service.isActive && (
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40">
                <div className="bg-red-500 text-white px-4 py-2 rounded-md font-medium shadow-md transform scale-110">
                  {t('services.statusFilter.inactive')}
                </div>
              </div>
            )}
          </div>
          
          <div className="p-4">
            <h3 className={`text-lg font-semibold mb-1 truncate ${!service.isActive ? 'text-gray-500 dark:text-gray-400' : ''}`}>{service.name}</h3>
            <p className={`text-sm mb-2 line-clamp-2 h-10 ${service.isActive ? 'text-gray-600 dark:text-gray-400' : 'text-gray-500 dark:text-gray-500'}`}>
              {service.description || t('services.noDescription')}
            </p>
            
            <div className="flex justify-between items-center mt-2">
              <div className="flex-1">
                <span className={`font-bold ${service.isActive ? 'text-primary' : 'text-gray-500 dark:text-gray-400'}`}>{service.price ? `${parseFloat(service.price.toString()).toFixed(2)} €` : '0.00 €'}</span>

                {/* Affichage de la durée si le service nécessite une réservation */}
                {service.requiresBooking && service.duration && (
                  <div className="flex items-center mt-1 text-xs text-gray-500 dark:text-gray-400">
                    <Clock size={12} className="mr-1" />
                    <span>
                      {service.duration >= 60
                        ? `${Math.floor(service.duration / 60)}h${service.duration % 60 > 0 ? service.duration % 60 : ''}`
                        : `${service.duration} min`}
                    </span>
                  </div>
                )}

                {/* Indication si le service ne nécessite pas de réservation */}
                {!service.requiresBooking && (
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 italic">
                    {t('services.noBookingRequired')}
                  </div>
                )}

                {/* Badge collaborateurs */}
                {service.collaborators && service.collaborators.length > 0 && (
                  <div className="relative mt-2" ref={showCollaboratorsPopover === service.id ? popoverRef : null}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowCollaboratorsPopover(showCollaboratorsPopover === service.id ? null : service.id);
                      }}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                      title={t('services.viewCollaborators') || 'Voir les collaborateurs'}
                    >
                      <Users size={12} />
                      <span>{service.collaborators.length}</span>
                    </button>

                    {/* Popover */}
                    {showCollaboratorsPopover === service.id && (
                      <div className="absolute bottom-full left-0 mb-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
                        <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                          <h6 className="font-medium text-sm">{t('services.assignedCollaborators')}</h6>
                        </div>
                        <div className="max-h-64 overflow-y-auto">
                          {service.collaborators.map((collaborator) => (
                            <div
                              key={collaborator.collaboratorCompagnieId}
                              className="p-3 border-b border-gray-100 dark:border-gray-700 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-750"
                            >
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium text-sm overflow-hidden">
                                  {collaborator.avatar ? (
                                    <img
                                      src={collaborator.avatar}
                                      alt={`${collaborator.firstName} ${collaborator.lastName}`}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <span>{collaborator.firstName?.charAt(0)}{collaborator.lastName?.charAt(0)}</span>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">
                                    {collaborator.firstName} {collaborator.lastName}
                                  </p>
                                  {collaborator.profession && (
                                    <p className="text-xs text-gray-500 truncate">
                                      {collaborator.profession}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="flex space-x-1">
                <button 
                  onClick={(e) => { e.stopPropagation(); viewModel.viewService(service); }}
                  className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
                  title={t('services.view') || 'Voir'}
                >
                  <Eye size={16} />
                </button>
                <button 
                  onClick={(e) => { e.stopPropagation(); viewModel.editService(service); }}
                  className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-primary dark:text-primary-400"
                  title={t('services.edit') || 'Modifier'}
                >
                  <Pencil size={16} />
                </button>
                <button 
                  onClick={(e) => { e.stopPropagation(); if (confirmDelete === service.id) {
                    viewModel.deleteService(service.id);
                    setConfirmDelete(null);
                  } else {
                    // Demander confirmation
                    setConfirmDelete(service.id);
                  }; }}
                  className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-red-500 dark:text-red-400"
                  title={confirmDelete === service.id ? (t('services.confirmDelete') || 'Confirmer la suppression') : (t('services.delete') || 'Supprimer')}
                >
                  <Trash2 size={16} />
                </button>
                <div 
                  onClick={(e) => e.stopPropagation()} 
                  className="flex items-center ml-1" 
                  title={service.isActive ? t('services.deactivate') || 'Désactiver' : t('services.activate') || 'Activer'}
                >
                  <Switch
                    id={`service-status-${service.id}`}
                    name={`service-status-${service.id}`}
                    label=""
                    defaultChecked={service.isActive}
                    onChange={(checked) => {
                      viewModel.toggleServiceStatus(service.id, checked);
                    }}
                    className="scale-75 origin-left"
                  />
                </div>
              </div>
            </div>
            
            {confirmDelete === service.id && (
              <div className="mt-2 border-t pt-2 border-gray-200 dark:border-gray-700">
                <p className="text-sm text-red-500 mb-2">{t('services.deleteConfirmation')}</p>
                <div className="flex justify-end space-x-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={(e) => { e.stopPropagation(); setConfirmDelete(null); }}
                  >
                    {t('services.cancel')}
                  </Button>
                  <Button 
                    variant="primary" 
                    size="sm" 
                    onClick={(e) => { e.stopPropagation(); viewModel.deleteService(service.id); setConfirmDelete(null); }}
                  >
                    {t('services.delete')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
      
      {services.length === 0 && (
        <div className="col-span-full flex flex-col items-center justify-center py-24 text-center bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-dashed border-gray-300 dark:border-gray-700 my-4">
          <div className="w-16 h-16 mb-4 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
            <Clock size={32} className="text-gray-400 dark:text-gray-500" />
          </div>
          <h3 className="text-xl font-medium text-gray-700 dark:text-gray-300 mb-2">{t('services.noServices')}</h3>
        </div>
      )}
    </div>
  );
});

export default ServiceGrid;
